import type { ReactNode } from "react"
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { differenceInMilliseconds } from "date-fns"
import {
	IconChevronDownOutline18 as ChevronDown,
	IconChevronUpOutline18 as ChevronUp,
	IconSquaresConnectedOutline18 as SquaresConnected,
} from "nucleo-ui-outline-18"

import type { EmailContact } from "@productlane/api"
import type { ConnectorAttribution } from "@productlane/lib/src/ai"
import { cn } from "@productlane/lib/src/browser"
import {
	AttachmentsList,
	Badge,
	formatAttachments,
	formatEmailContacts,
	IconButton,
	IdentityTooltipContent,
	Popover,
	shouldShowIdentityTooltip,
	Tooltip,
} from "@productlane/ui"

import type { TimelineEventProps } from "./types"
import {
	MESSAGE_UNDO_CANCELLATION_TIMEOUT,
	useUndoContext,
} from "@/context/undo-context"
import { emailBody, emailSignatureInfo, emailTextFlags } from "@/lib/email-body"
import { useZeroWorkspace } from "@/lib/zero"
import { useThreadContext } from "../../../thread-context"
import { AgentMessageAvatar } from "./base/agent-message-avatar"
import { SlackStyleMessage } from "./base/slack-style-message"
import { CollapsibleMailContent } from "./collapsible-mail-content"
import { DeviceInfoLine } from "./device-info-line"
import { AI_CHAT_ACTOR_NAME, isAgentEmailReply } from "./improve-reply"
import { ImproveReplyButton } from "./improve-reply-panel"

//? Right now we will filter out super small images ("templated content") to prevent showing small icons
//? But is there any other good solution to this? I dont know, couldnt find any...
const MIN_IMAGE_SIZE = 5000 // (5 KB)

export function EmailEvent({
	event,
	showAvatar = true,
	isFirstMessage = false,
	deviceInfoText,
}: TimelineEventProps) {
	const { setPendingMessage } = useUndoContext()
	const { thread } = useThreadContext()
	const workspace = useZeroWorkspace()

	// With several inboxes the sender line alone no longer says which address
	// a message went out from.
	const hasMultipleAddresses = useMemo(
		() =>
			(workspace?.emailSettings ?? []).filter((address) => address.isEnabled)
				.length > 1,
		[workspace?.emailSettings],
	)

	const companyLogoUrl = thread.company?.logoUrl || undefined
	const companyName = thread.company?.name || undefined

	useEffect(() => {
		if (event.type !== "email") return
		if (event.email.type === "INBOUND") return

		// When reloading the page, existing pending emails should be shown
		const pendingTime = differenceInMilliseconds(
			new Date(),
			new Date(event.email.createdAt),
		)
		if (pendingTime <= MESSAGE_UNDO_CANCELLATION_TIMEOUT) {
			setPendingMessage(
				event.email.id,
				pendingTime,
				emailBody(event.email).html,
			)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [event.type])

	// I found differently formatted attachments in the database, so we need to handle both cases
	// TODO: fix cleanly
	const attachments = useMemo(() => {
		if (event.type !== "email") return []
		const attachmentsValue = event.email.attachments
		if (!attachmentsValue || !Array.isArray(attachmentsValue)) return []
		return attachmentsValue as unknown[]
	}, [event])

	const formattedAttachments = useMemo(
		() => formatAttachments(attachments),
		[attachments],
	)

	// Read from the unpopulated body so cid: references survive for the filter below.
	const emailHtml = event.type === "email" ? emailBody(event.email).html : ""
	const { hidden: hiddenContentHtml, cids: signatureCids } =
		emailSignatureInfo(emailHtml)
	const { hasText, isForwarded } = emailTextFlags(emailHtml)

	const sortedFilteredAttachments = useMemo(() => {
		return formattedAttachments
			.sort((a, b) => {
				if (a.ContentType.includes("video")) return -1
				if (b.ContentType.includes("video")) return 1
				if (a.ContentType.includes("image")) return -1
				if (b.ContentType.includes("image")) return 1
				return 0
			})
			.filter(({ ContentLength, ContentType, ContentID, Content, Name }) => {
				const fileSize = Number(ContentLength) || 0
				const fileType = ContentType || ""

				// Legacy base64 blobs are never rendered; only URL-backed attachments load over HTTP.
				if (Content && !Content.startsWith("http")) return false

				// Filter out signature images by checking if their CID is in the hidden content
				if (ContentID && signatureCids.size > 0) {
					const isSignatureImage = Array.from(signatureCids).some(
						(cid) => ContentID.includes(cid) || cid.includes(ContentID),
					)
					if (isSignatureImage) return false
				}

				// Also check if the image content (base64) appears in the hidden signature HTML
				// This catches images that are embedded directly without CID references
				// Skip this check for CDN URLs since they are legitimate uploaded attachments
				const isCdnUrl = Content?.includes("uploads.productlane.com")
				if (
					hiddenContentHtml &&
					!isCdnUrl &&
					(fileType.includes("image") || fileType.includes("video"))
				) {
					// Check if the base64 content appears in the hidden HTML
					if (Content && hiddenContentHtml.includes(Content.slice(0, 100))) {
						return false
					}
					// Check if filename appears in hidden content (some signatures reference by name)
					if (
						Name &&
						hiddenContentHtml.toLowerCase().includes(Name.toLowerCase())
					) {
						return false
					}
				}

				// Drop tracking pixels only when the size is known and below the threshold; an unknown size (0) is not a spacer.
				return (
					!(fileType.includes("video") || fileType.includes("image")) ||
					fileSize === 0 ||
					fileSize >= MIN_IMAGE_SIZE
				)
			})
	}, [formattedAttachments, signatureCids, hiddenContentHtml])

	if (event.type !== "email") return null

	// Only auto-expand the first message when it was forwarded; a regular first
	// email keeps its quoted history and signature collapsed.
	const expandByDefault = isFirstMessage && isForwarded

	const TOs = formatEmailContacts(event.email.to)
	const CCs = formatEmailContacts(event.email.cc)
	const BCCs = formatEmailContacts(event.email.bcc)
	const FROMs =
		hasMultipleAddresses && event.email.type === "OUTBOUND"
			? formatEmailContacts(event.email.from ? [event.email.from] : [])
			: []
	const isFromAgent =
		isAgentEmailReply(event.email) ||
		(event.email.type === "OUTBOUND" && event.email.source === "AUTO_REPLY")
	const senderEmail = (event.email.from as EmailContact | undefined)?.email
	const connectorSources =
		event.type === "email" &&
		(event.email.source === "AI" || event.email.source === "AUTO_REPLY")
			? (event.connectorSources ?? [])
			: []

	return (
		<SlackStyleMessage
			showAvatar={showAvatar}
			senderName={
				isFromAgent ? AI_CHAT_ACTOR_NAME : event.email.fromContactName
			}
			avatarContent={isFromAgent ? <AgentMessageAvatar /> : undefined}
			senderEmail={senderEmail}
			senderImage={event.email.fromContactImage}
			timestamp={new Date(event.email.date)}
			companyName={companyName}
			companyLogoUrl={companyLogoUrl}
			source="email"
			isOutbound={event.email.type === "OUTBOUND"}
			footer={
				deviceInfoText ? <DeviceInfoLine text={deviceInfoText} /> : undefined
			}
		>
			{connectorSources.length > 0 && (
				<AutoReplyConnectorsBadge connectors={connectorSources} />
			)}
			{FROMs.length > 0 && <EmailContactsList emails={FROMs} label={"From"} />}
			{TOs.length > 0 && <EmailContactsList emails={TOs} label={"To"} />}
			{CCs.length > 0 && <EmailContactsList emails={CCs} label={"Cc"} />}
			{BCCs.length > 0 && <EmailContactsList emails={BCCs} label={"Bcc"} />}

			<div className="items-center justify-between gap-1">
				<div className="flex-wrap break-words">
					{emailHtml ? (
						<CollapsibleMailContent
							htmlContent={emailHtml}
							attachments={formattedAttachments}
							defaultExpanded={expandByDefault}
							authoredInProductlane={event.email.type === "OUTBOUND"}
						/>
					) : (
						<pre className="font-450 whitespace-pre-wrap !font-sans text-[0.875rem]">
							{emailBody(event.email).text}
						</pre>
					)}
				</div>
			</div>

			{sortedFilteredAttachments && sortedFilteredAttachments.length > 0 && (
				<>
					{hasText && emailHtml !== "<br>" && <hr className="my-3" />}
					<AttachmentsList
						enableViewer
						showPointer={false}
						attachments={sortedFilteredAttachments.map((attachment) => ({
							...attachment,
							Content: attachment.Content ?? "",
						}))}
					/>
				</>
			)}

			{isAgentEmailReply(event.email) && (
				<div className="mt-2">
					<ImproveReplyButton
						threadId={thread.id}
						channel="email"
						messageId={event.email.id}
					/>
				</div>
			)}
		</SlackStyleMessage>
	)
}

// An unattended reply has no composer to hang attribution on, so the sent
// message carries what the agent read while writing it.
const AutoReplyConnectorsBadge = ({
	connectors,
}: {
	connectors: readonly ConnectorAttribution[]
}) => {
	const [open, setOpen] = useState(false)
	return (
		<div className="mb-1.5 flex">
			<Popover
				open={open}
				onOpenChange={setOpen}
				align="start"
				className="w-64 p-1"
				content={
					<div className="flex flex-col">
						<div className="px-1.5 pb-1.5 pt-0.5 text-[10.5px] font-medium tracking-[0.08em] text-gray-400 dark:text-gray-500">
							Connectors used
						</div>
						{connectors.map((connector) => (
							<div
								key={`${connector.sectionId}-${connector.tool}`}
								className="flex items-center gap-2 rounded-md px-1.5 py-1"
							>
								<SquaresConnected className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
								<span className="truncate text-[12.5px] text-gray-700 dark:text-gray-200">
									{connector.title}
								</span>
								<span className="ml-auto shrink-0 font-mono text-[10.5px] text-gray-400 dark:text-gray-500">
									{connector.ok ? connector.tool : `${connector.tool} failed`}
								</span>
							</div>
						))}
					</div>
				}
			>
				<button type="button" className="cursor-default">
					<Badge variant="default" size="sm" Icon={SquaresConnected}>
						{connectors.length === 1
							? connectors[0]!.title
							: `${connectors.length} connectors`}
					</Badge>
				</button>
			</Popover>
		</div>
	)
}

const COLLAPSED_HEIGHT = 18 // single line height for text-[12px]

export const EmailContactsList = ({
	emails,
	label,
}: {
	emails: EmailContact[]
	label: string
}) => {
	const [collapsed, setIsCollapsed] = useState(true)
	const contentRef = useRef<HTMLDivElement>(null)
	const [fullHeight, setFullHeight] = useState<number | undefined>(undefined)
	const [isCollapsible, setIsCollapsible] = useState(false)

	useLayoutEffect(() => {
		const el = contentRef.current
		if (!el) return
		const height = el.scrollHeight
		setFullHeight(height)
		setIsCollapsible(height > COLLAPSED_HEIGHT + 1)
	}, [emails])

	return (
		<div
			className={cn(
				"relative mb-0.5 flex h-fit w-full items-start text-[12px] text-secondary",
				isCollapsible && "pr-5",
			)}
		>
			<div
				ref={contentRef}
				className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5 overflow-hidden whitespace-pre-wrap break-all transition-[max-height] duration-200 ease-in-out"
				style={{
					maxHeight:
						collapsed && isCollapsible
							? COLLAPSED_HEIGHT
							: (fullHeight ?? COLLAPSED_HEIGHT),
				}}
			>
				<span className="mr-1 text-[11px] tracking-wide text-tertiary">
					{label}
				</span>
				{emails.map((e, i) => {
					const tooltipLabel: ReactNode = e.name ? (
						<IdentityTooltipContent name={e.name} email={e.email} />
					) : (
						e.email
					)

					return (
						<Tooltip
							key={`${i}-${e.email}`}
							label={tooltipLabel}
							enabled={shouldShowIdentityTooltip({
								visibleText: e.email,
								detail: e.name,
							})}
						>
							<span>
								{e.email}
								{i !== emails.length - 1 ? "," : ""}
							</span>
						</Tooltip>
					)
				})}
			</div>
			{isCollapsible && (
				<IconButton
					Icon={collapsed ? ChevronDown : ChevronUp}
					variant="link"
					className="absolute right-0 top-0.5 h-3 min-h-3 w-3 min-w-3 text-tertiary transition-transform duration-200 hover:text-secondary"
					onClick={() => {
						if (contentRef.current) {
							setFullHeight(contentRef.current.scrollHeight)
						}
						setIsCollapsed((value) => !value)
					}}
				/>
			)}
		</div>
	)
}
